import {
  EmailAvailability,
  type InviteByEmailBody,
  InviteByEmailResult,
  InviteEmailStatuses,
  ResetTokenCheckResult,
  TestEmailResult,
} from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { Schema } from "effect"
import { apiJson } from "./api"
import { authClient } from "./auth-client"

/**
 * The email routes (decision 0064, wave M spec §5.9, §6.1): whether this Minga can send email, invite by email,
 * resend, the per-invitation email state and the test email. Every answer is decoded with the protocol schemas the
 * cloud encodes with. Errors arrive as `ApiError` with `body.error` (and `body.problem` / `body.resendAt`).
 */

const enc = encodeURIComponent
const ws = (workspaceId: string) => `/api/workspaces/${enc(workspaceId)}`

/** A fresh id per click: the cloud's idempotency key for the email that click sends (32 hex characters). */
export const newRequestId = (): string => crypto.randomUUID().replaceAll("-", "")

/** GET /api/me/email: whether email is set up on this Minga at all. */
export const emailAvailabilityQuery = queryOptions({
  queryKey: ["me", "email"],
  queryFn: () => apiJson("/api/me/email", EmailAvailability),
  staleTime: 300_000,
})

/** GET …/invitations/email-status (owner/admin): the latest email per invitation. */
export const inviteEmailStatusQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "invitations", "email-status"],
    queryFn: () => apiJson(`${ws(workspaceId)}/invitations/email-status`, InviteEmailStatuses),
    staleTime: 30_000,
  })

/** POST …/invitations/email: creates the invitation and emails it. The invitation exists whatever the email did. */
export const inviteByEmail = (workspaceId: string, body: InviteByEmailBody) =>
  apiJson(`${ws(workspaceId)}/invitations/email`, InviteByEmailResult, { method: "POST", body })

/** Decision 0129/0130: PUT …/invitations/:id/projects, the projects a copy-link invitation joins on accept. */
export const setInvitationProjects = (
  workspaceId: string,
  invitationId: string,
  projectIds: ReadonlyArray<string>,
) =>
  apiJson(
    `${ws(workspaceId)}/invitations/${enc(invitationId)}/projects`,
    Schema.Struct({ projectIds: Schema.Array(Schema.String) }),
    { method: "PUT", body: { projectIds } },
  )

/** POST …/invitations/:id/resend: emails a pending, unexpired invitation again (and extends it by 7 days). */
export const resendInviteEmail = (workspaceId: string, invitationId: string) =>
  apiJson(`${ws(workspaceId)}/invitations/${enc(invitationId)}/resend`, InviteByEmailResult, {
    method: "POST",
    body: { requestId: newRequestId() },
  })

/**
 * Decision 0130 §7: GET …/invitations/:id/link (owner/admin), a pending invitation's link with its key (`?k=`), which
 * only the cloud can make (an HMAC under the auth secret).
 */
export const invitationLink = async (workspaceId: string, invitationId: string): Promise<string> =>
  (
    await apiJson(
      `${ws(workspaceId)}/invitations/${enc(invitationId)}/link`,
      Schema.Struct({ link: Schema.String }),
    )
  ).link

/** POST /api/me/test-email: a test email to the signed-in person's own address (the body carries no address). */
export const sendTestEmail = () =>
  apiJson("/api/me/test-email", TestEmailResult, { method: "POST", body: { requestId: newRequestId() } })

/**
 * POST /api/password-reset/check: whether the held reset token still works (exists, unexpired), so /reset-password
 * says "expired or already used" before anyone types a password. The token goes in the body, never a URL. Not retried.
 */
export const resetTokenCheckQuery = (token: string) =>
  queryOptions({
    queryKey: ["password-reset", "check", token],
    queryFn: () =>
      apiJson("/api/password-reset/check", ResetTokenCheckResult, { method: "POST", body: { token } }),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  })

/** Where the reset email's link lands (the cloud builds the link itself; Better Auth only checks this is our origin). */
export const RESET_PASSWORD_PATH = "/reset-password"

/**
 * Asks Better Auth to email a reset link (POST /api/auth/request-password-reset). The answer is the same whether or
 * not the address has an account; null when it was accepted, else the HTTP status (429: too many from this IP).
 */
export const requestPasswordReset = async (
  email: string,
): Promise<{ readonly status: number | null } | null> => {
  const { error } = await authClient.requestPasswordReset({ email, redirectTo: RESET_PASSWORD_PATH })
  return error ? { status: error.status ?? null } : null
}
